import type { QueryClient } from "@tanstack/react-query";

import { refusAccesSession } from "./disponibilite-session.js";
import { sessionCouranteProuvee } from "./mutation-session-recovery.js";
import { ME_QUERY_KEY } from "./use-me.js";

const PREFIXE_PREPARATION_MFA = ["auth", "setupMfa"] as const;

/** Le secret TOTP appartient à un compte, jamais à un cache global partagé. */
export const clePreparationMfa = (utilisateurId: string | null) =>
  [...PREFIXE_PREPARATION_MFA, utilisateurId] as const;

export function retirerPreparationsMfaAutresIdentites(queryClient: QueryClient, me: unknown) {
  const utilisateurId = sessionCouranteProuvee(me)
    ? (me as { utilisateur: { id: string } }).utilisateur.id
    : null;
  queryClient.removeQueries({
    queryKey: PREFIXE_PREPARATION_MFA,
    predicate: (query) => utilisateurId === null || query.queryKey[2] !== utilisateurId,
  });
}

/**
 * Retire la preuve immédiatement, avant la quarantaine asynchrone. TanStack
 * garde sinon data A après un refus, puis remplace error par la panne suivante.
 * Seul un nouveau succès réseau peut alors remplir de nouveau auth.me.data.
 */
export function retirerPreuveIdentiteSession(queryClient: QueryClient): string | null {
  const query = queryClient.getQueryCache().find({ queryKey: ME_QUERY_KEY, exact: true });
  const me = query?.state.data;
  const utilisateurId = sessionCouranteProuvee(me)
    ? (me as { utilisateur: { id: string } }).utilisateur.id
    : null;
  // cancel() neutralise le fetch synchroniquement. Sans revert:false, un
  // cancelQueries ultérieur pourrait restaurer A depuis son ancien snapshot.
  // Ne pas utiliser silent:true : TanStack pourrait alors faire suivre à
  // l'ancien appel la promesse d'une nouvelle identité. La réponse transport
  // tardive est ignorée même si le client HTTP ne respecte pas AbortSignal.
  void query?.cancel({ revert: false });
  query?.setState({ data: undefined, dataUpdatedAt: 0 });
  retirerPreparationsMfaAutresIdentites(queryClient, undefined);
  return utilisateurId;
}

/** Un 403 métier n'est pas une révocation ; un refus de auth.me, si. */
export function invaliderPreuveSessionApresRefus(
  queryClient: QueryClient,
  error: unknown,
  queryKey: readonly unknown[],
): { utilisateurId: string | null } | null {
  const refus = refusAccesSession(error);
  const concerneIdentite = queryKey.length === 1 && queryKey[0] === ME_QUERY_KEY[0];
  if (!refus || (!concerneIdentite && refus !== "UNAUTHORIZED")) return null;
  return { utilisateurId: retirerPreuveIdentiteSession(queryClient) };
}
